import { useMemo, useState } from 'react';
import { useDateTime } from '../hooks/useDateTime';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge, Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Input, Select, Spinner, Text,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { useT } from '../i18n';
import { EmployeeImportDialog } from '../components/attendance/EmployeeImportDialog';
import { AttendancePhotoGrid } from '../components/AttendancePhotoGrid';
import { usePermissions } from '../hooks/usePermission';
import { attendanceErrorMessage } from '../utils/attendance-error';
import { fetchUsers } from '../api/settings.api';
import {
  createCrew, createEmployee, decidePreBaja, deleteBiometrics, deleteEmployee, updateEmployee, fetchCrews, fetchEmployees, deleteCrew, fetchEnrollPhotos, setEmployeeCrew, setEmployeeUser,
  type DeletionReceipt, type EmployeeRow,
} from '../api/attendance.api';
import { PageTitle } from '../components/shared/PageTitle';
import { FilterPanel } from '../components/shared/FilterPanel';
import { DataTable } from '../components/shared/DataTable';
import { CatalogSelect } from '../components/shared/CatalogSelect';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  card: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, padding: tokens.spacingVerticalM, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium },
  row: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', alignItems: 'center' },
  muted: { color: tokens.colorNeutralForeground3 },
  danger: { color: tokens.colorPaletteRedForeground1 },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '720px' },
  th: { textAlign: 'left', padding: tokens.spacingVerticalXS, borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, color: tokens.colorNeutralForeground3, fontWeight: 600, fontSize: tokens.fontSizeBase200 },
  td: { padding: tokens.spacingVerticalS, borderBottom: `1px solid ${tokens.colorNeutralStroke3}`, verticalAlign: 'middle' },
});

const STATUS_COLOR = { ACTIVE: 'success', PRE_TERMINATION: 'warning', INACTIVE: 'danger' } as const;

/** Padrón de personal: alta, importación desde Excel, pre-bajas y borrado de datos biométricos. */
export function PersonnelPage() {
  const t = useT();
  const a = t.attendancePage;
  const s = useStyles();
  const fmt = useDateTime();
  const { tenantId } = useParams<{ tenantId: string }>();
  const qc = useQueryClient();
  const { permissions } = usePermissions();
  const canEnroll = permissions.has('attendance:enroll');
  const canDecide = permissions.has('attendance:prebaja_decide');
  const canDelete = permissions.has('attendance:delete_biometrics');

  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [number, setNumber] = useState('');
  const [crewId, setCrewId] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [fQuery, setFQuery] = useState('');
  const [fCrew, setFCrew] = useState<string[]>([]);
  const [fStatus, setFStatus] = useState<string[]>([]);
  const [fEnroll, setFEnroll] = useState<string[]>([]);
  const [target, setTarget] = useState<EmployeeRow | null>(null);
  const [photosOf, setPhotosOf] = useState<EmployeeRow | null>(null);
  const enrollPhotos = useQuery({ queryKey: ['att-enroll-photos', tenantId, photosOf?.employeeId], queryFn: () => fetchEnrollPhotos(tenantId!, photosOf!.employeeId), enabled: !!tenantId && !!photosOf });
  const [confirmName, setConfirmName] = useState('');
  const [reason, setReason] = useState('');
  const [receipt, setReceipt] = useState<DeletionReceipt | null>(null);

  const list = useQuery({ queryKey: ['employees', tenantId], queryFn: () => fetchEmployees(tenantId!), enabled: !!tenantId, retry: false });
  const crews = useQuery({ queryKey: ['att-crews', tenantId], queryFn: () => fetchCrews(tenantId!), enabled: !!tenantId, retry: false });
  const users = useQuery({ queryKey: ['att-link-users', tenantId], queryFn: fetchUsers, enabled: !!tenantId && canEnroll, retry: false });
  const userLabel = (id?: string) => { const u = users.data?.find((x) => x.userId === id); return u ? (u.name || u.email || u.phoneNumber) : undefined; };
  const crewOptions = (crews.data ?? []).map((c) => ({ id: c.crewId, label: c.name }));
  const deleteCrewNow = async (id: string) => {
    try {
      await deleteCrew(tenantId!, id);
      setError('');
      if (crewId === id) setCrewId('');
      await qc.invalidateQueries({ queryKey: ['att-crews', tenantId] });
    } catch (e) { fail(e); }
  };
  const crewName = (id?: string) => crews.data?.find((c) => c.crewId === id)?.name;
  const refresh = () => qc.invalidateQueries({ queryKey: ['employees', tenantId] });
  const fail = (e: unknown) => setError(attendanceErrorMessage(e, a.errors, a.genericError));

  const add = useMutation({
    mutationFn: () => createEmployee(tenantId!, { name: name.trim(), employeeNumber: number.trim() || undefined, crewId: crewId || undefined }),
    onSuccess: () => { setName(''); setNumber(''); setError(''); void refresh(); },
    onError: fail,
  });
  const createCrewNow = async (crewName: string) => {
    try {
      const c = await createCrew(tenantId!, crewName.trim());
      setError('');
      await qc.invalidateQueries({ queryKey: ['att-crews', tenantId] });
      return c.crewId;
    } catch (e) { fail(e); return ''; }
  };
  const assign = useMutation({
    mutationFn: ({ id, crew }: { id: string; crew: string }) => setEmployeeCrew(tenantId!, id, crew),
    onSuccess: () => { setError(''); void refresh(); },
    onError: fail,
  });
  const linkUser = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) => setEmployeeUser(tenantId!, id, userId),
    onSuccess: () => { setError(''); void refresh(); },
    onError: fail,
  });
  const decide = useMutation({
    mutationFn: ({ id, d, e_status }: { id: string; d: 'KEEP' | 'TERMINATE'; e_status?: string }) => decidePreBaja(tenantId!, id, d, d === 'KEEP' ? 'Se mantiene por decisión de RH' : e_status === 'ACTIVE' ? 'Baja manual' : 'Baja por inasistencias'),
    onSuccess: () => { setError(''); void refresh(); },
    onError: fail,
  });
  const [editing, setEditing] = useState<EmployeeRow | null>(null);
  const [eName, setEName] = useState('');
  const [eNumber, setENumber] = useState('');
  const [eCrew, setECrew] = useState('');
  const edit = useMutation({
    mutationFn: () => updateEmployee(tenantId!, editing!.employeeId, { name: eName.trim(), employeeNumber: eNumber.trim(), crewId: eCrew }),
    onSuccess: () => { setEditing(null); setError(''); void refresh(); },
    onError: fail,
  });
  const [delPerson, setDelPerson] = useState<EmployeeRow | null>(null);
  const [delName, setDelName] = useState('');
  const [delReason, setDelReason] = useState('');
  const delEmp = useMutation({
    mutationFn: () => deleteEmployee(tenantId!, delPerson!.employeeId, delName, delReason.trim()),
    onSuccess: () => { setDelPerson(null); setDelName(''); setDelReason(''); setError(''); void refresh(); },
    onError: (e) => { setDelPerson(null); fail(e); },
  });
  const del = useMutation({
    mutationFn: () => deleteBiometrics(tenantId!, target!.employeeId, confirmName, reason.trim()),
    onSuccess: (r) => { setTarget(null); setConfirmName(''); setReason(''); setReceipt(r); setError(''); void refresh(); },
    onError: fail,
  });

  const filtered = useMemo(() => {
    const q = fQuery.trim().toLowerCase();
    return (list.data ?? []).filter((e) => {
      if (q && !e.name.toLowerCase().includes(q) && !(e.employeeNumber ?? '').toLowerCase().includes(q)) return false;
      if (fCrew.length && !fCrew.includes(e.crewId ?? '__none')) return false;
      if (fStatus.length && !fStatus.includes(e.status)) return false;
      if (fEnroll.length) {
        const state = e.enrollment.status === 'ENROLLED' ? 'ENROLLED' : e.consent ? 'PENDING' : 'NO_CONSENT';
        if (!fEnroll.includes(state)) return false;
      }
      return true;
    });
  }, [list.data, fQuery, fCrew, fStatus, fEnroll]);
  const clearFilters = () => { setFQuery(''); setFCrew([]); setFStatus([]); setFEnroll([]); };

  // El servidor responde 403 FEATURE_DISABLED si la empresa no activó el módulo.
  const disabled = (list.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error === 'FEATURE_DISABLED';
  if (disabled) return <div className={s.page}><Text>{a.disabled}</Text></div>;

  return (
    <div className={s.page}>
      <div>
        <PageTitle>{a.personnelTitle}</PageTitle>
        <Text className={s.muted} block>{a.subtitlePersonnel}</Text>
      </div>
      {error && <Text className={s.danger} role="alert">{error}</Text>}

      {canEnroll && (
        <div className={s.card}>
          <Text weight="semibold">{a.addPerson}</Text>
          <div className={s.row}>
            <Input style={{ flex: '1 1 340px', minWidth: 260 }} aria-label={a.fullName} placeholder={a.fullName} value={name} onChange={(_, d) => setName(d.value)} />
            <Input style={{ width: 120 }} aria-label={a.employeeNumber} placeholder={a.numberShort} value={number} onChange={(_, d) => setNumber(d.value)} />
            <CatalogSelect ariaLabel={a.crewHint} options={crewOptions} value={crewId} onChange={setCrewId} emptyLabel={a.noCrew} onCreate={createCrewNow} onDelete={canEnroll ? deleteCrewNow : undefined} deleteConfirm={a.deleteCrewConfirm} />
            <Button appearance="primary" disabled={name.trim().split(/\s+/).length < 2 || add.isPending} onClick={() => add.mutate()}>{a.add}</Button>
          </div>
        </div>
      )}

      <div className={s.row}>
        <Input aria-label={a.filterSearch} placeholder={a.filterSearch} value={fQuery} onChange={(_, d) => setFQuery(d.value)} />
        <FilterPanel
          onClearAll={clearFilters}
          dimensions={[
            { key: 'crew', label: a.colCrew, selected: fCrew, onApply: setFCrew, onClear: () => setFCrew([]),
              options: [{ value: '__none', label: a.noCrew }, ...crewOptions.map((c) => ({ value: c.id, label: c.label }))] },
            { key: 'status', label: a.colStatus, selected: fStatus, onApply: setFStatus, onClear: () => setFStatus([]),
              options: [{ value: 'ACTIVE', label: a.statusACTIVE }, { value: 'PRE_TERMINATION', label: a.statusPRE_TERMINATION }, { value: 'INACTIVE', label: a.statusINACTIVE }] },
            { key: 'enroll', label: a.colEnrollment, selected: fEnroll, onApply: setFEnroll, onClear: () => setFEnroll([]),
              options: [{ value: 'ENROLLED', label: a.enrolled }, { value: 'PENDING', label: a.filterPending }, { value: 'NO_CONSENT', label: a.noConsent }] },
          ]}
        />
        {canEnroll && <Button style={{ marginLeft: 'auto' }} onClick={() => setImportOpen(true)}>{a.importTitle}</Button>}
      </div>
      {tenantId && <EmployeeImportDialog open={importOpen} tenantId={tenantId} onClose={() => setImportOpen(false)} onImported={() => { void refresh(); }} />}

      {list.isLoading ? <Spinner size="small" label={a.loading} /> : (list.data?.length ?? 0) === 0 ? <Text className={s.muted}>{a.empty}</Text> : filtered.length === 0 ? <Text className={s.muted}>{a.noMatches}</Text> : (
        <div className={s.tableWrap}>
          <DataTable<EmployeeRow>
            rows={filtered}
            rowKey={(e) => e.employeeId}
            columns={[
              { key: 'name', header: a.colName, width: 1.4, render: (e) => <>{e.name}{e.employeeNumber ? <Text className={s.muted}> · {e.employeeNumber}</Text> : null}</> },
              { key: 'crew', header: a.colCrew, width: 1.3, render: (e) => (
                canEnroll && crews.data?.length ? (
                  <CatalogSelect size="small" ariaLabel={a.crewHint} options={crewOptions} value={e.crewId ?? ''} emptyLabel={a.noCrew} onChange={(v) => v && v !== e.crewId && assign.mutate({ id: e.employeeId, crew: v })} />
                ) : <Text className={s.muted}>{crewName(e.crewId) ?? a.noCrew}</Text>
              ) },
              { key: 'user', header: a.colUser, width: 1.4, render: (e) => (
                canEnroll && users.data?.length ? (
                  <Select size="small" aria-label={a.colUser} value={e.userId ?? ''} onChange={(_, d) => linkUser.mutate({ id: e.employeeId, userId: d.value })}>
                    <option value="">{a.noUser}</option>
                    {users.data.filter((u) => u.userId && (u.userId === e.userId || !list.data!.some((x) => x.userId === u.userId))).map((u) => (
                      <option key={u.userId} value={u.userId}>{u.name || u.email || u.phoneNumber}</option>
                    ))}
                  </Select>
                ) : <Text className={s.muted}>{userLabel(e.userId) ?? a.noUser}</Text>
              ) },
              { key: 'status', header: a.colStatus, render: (e) => <Badge appearance="tint" color={STATUS_COLOR[e.status]}>{a[`status${e.status}` as const]}</Badge> },
              { key: 'enrollment', header: a.colEnrollment, width: 1.4, render: (e) => (
                e.enrollment.status === 'ENROLLED' ? <Badge appearance="tint" color="success">{a.enrolled} · {e.enrollment.photos} {a.photosOf}</Badge>
                  : e.consent ? <Text>{e.enrollment.photos} {a.photosOf}</Text> : <Text className={s.muted}>{a.noConsent}</Text>
              ) },
              { key: 'misses', header: a.colMisses, width: 0.6, render: (e) => e.missCount },
              { key: 'actions', header: a.colActions, width: 2, render: (e) => (
<div className={s.row}>
          {canDecide && e.status === 'PRE_TERMINATION' && (
            <>
              <Button size="small" onClick={() => decide.mutate({ id: e.employeeId, d: 'KEEP' })}>{a.keep}</Button>
              <Button size="small" onClick={() => decide.mutate({ id: e.employeeId, d: 'TERMINATE' })}>{a.terminate}</Button>
            </>
          )}
          {canDecide && e.status === 'ACTIVE' && (
            <Button size="small" onClick={() => decide.mutate({ id: e.employeeId, d: 'TERMINATE', e_status: 'ACTIVE' })}>{a.terminate}</Button>
          )}
          {canDelete && (
            <Button size="small" onClick={() => { setDelPerson(e); setDelName(''); setDelReason(''); }}>{a.deletePerson}</Button>
          )}
          {canEnroll && !e.biometricsDeletedAt && e.enrollment.photos > 0 && (
            <Button size="small" onClick={() => setPhotosOf(e)}>{a.viewEnrollPhotos}</Button>
          )}
          {canDelete && !e.biometricsDeletedAt && (e.enrollment.photos > 0 || e.status === 'INACTIVE') && (
            <Button size="small" onClick={() => { setTarget(e); setConfirmName(''); setReason(''); }}>{a.deleteBiometrics}</Button>
          )}
        </div>
              ) },
            ]}
          />
        </div>
      )}

      <Dialog open={!!target} onOpenChange={(_, d) => { if (!d.open) setTarget(null); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{a.deleteTitle} {target?.name}</DialogTitle>
            <DialogContent>
              <Text block>{a.deleteBody}</Text>
              <div className={s.row} style={{ marginTop: tokens.spacingVerticalM }}>
                <Input aria-label={a.typeName} placeholder={a.typeName} value={confirmName} onChange={(_, d) => setConfirmName(d.value)} />
                <Input aria-label={a.reason} placeholder={a.reason} value={reason} onChange={(_, d) => setReason(d.value)} />
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTarget(null)}>{a.cancel}</Button>
              <Button appearance="primary" disabled={del.isPending || reason.trim().length < 3 || confirmName.trim().toLowerCase() !== target?.name.trim().toLowerCase()} onClick={() => del.mutate()}>{a.confirmDelete}</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(_, d) => { if (!d.open) setEditing(null); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{a.editTitle}</DialogTitle>
            <DialogContent>
              <div className={s.row}>
                <Input style={{ flex: '1 1 100%' }} aria-label={a.fullName} placeholder={a.fullName} value={eName} onChange={(_, d) => setEName(d.value)} />
                <Input style={{ width: 120 }} aria-label={a.employeeNumber} placeholder={a.numberShort} value={eNumber} onChange={(_, d) => setENumber(d.value)} />
                <Select aria-label={a.colCrew} value={eCrew} onChange={(_, d) => setECrew(d.value)}>
                  <option value="">{a.noCrew}</option>
                  {crewOptions.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </Select>
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditing(null)}>{a.cancel}</Button>
              <Button appearance="primary" disabled={edit.isPending || eName.trim().split(/\s+/).length < 2} onClick={() => edit.mutate()}>{a.save}</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={!!delPerson} onOpenChange={(_, d) => { if (!d.open) setDelPerson(null); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{a.deletePersonTitle} {delPerson?.name}</DialogTitle>
            <DialogContent>
              <Text block>{a.deletePersonBody}</Text>
              <div className={s.row} style={{ marginTop: tokens.spacingVerticalM }}>
                <Input aria-label={a.typeName} placeholder={a.typeName} value={delName} onChange={(_, d) => setDelName(d.value)} />
                <Input aria-label={a.reason} placeholder={a.reason} value={delReason} onChange={(_, d) => setDelReason(d.value)} />
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDelPerson(null)}>{a.cancel}</Button>
              <Button appearance="primary" disabled={delEmp.isPending || delReason.trim().length < 3 || delName.trim().toLowerCase() !== delPerson?.name.trim().toLowerCase()} onClick={() => delEmp.mutate()}>{a.deletePerson}</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={!!photosOf} onOpenChange={(_, d) => { if (!d.open) setPhotosOf(null); }}>
        <DialogSurface style={{ maxWidth: '640px' }}>
          <DialogBody>
            <DialogTitle>{a.enrollPhotosTitle} {photosOf?.name}</DialogTitle>
            <DialogContent>
              <AttendancePhotoGrid photos={enrollPhotos.data} loading={enrollPhotos.isLoading} empty={a.noEnrollPhotos} />
            </DialogContent>
            <DialogActions><Button appearance="primary" onClick={() => setPhotosOf(null)}>{a.close}</Button></DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={!!receipt} onOpenChange={(_, d) => { if (!d.open) setReceipt(null); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{a.receiptTitle}</DialogTitle>
            <DialogContent>
              {receipt && (
                <>
                  <Text block weight="semibold">{receipt.employeeName ?? target?.name}</Text>
                  <Text block>{a.receiptId}: {receipt.deletionId}</Text>
                  <Text block>{fmt.dateTime(receipt.executedAt)}</Text>
                  <Text block>{a.receiptFaces}: {receipt.facesDeleted}</Text>
                  <Text block>{a.receiptEnroll}: {receipt.enrollPhotosDeleted}</Text>
                  <Text block>{a.receiptAttendance}: {receipt.attendancePhotosDeleted}</Text>
                </>
              )}
            </DialogContent>
            <DialogActions><Button appearance="primary" onClick={() => setReceipt(null)}>{a.close}</Button></DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
